import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function AffiliatePage() {
  const { config: currencyConfig } = useCurrency();
  const [status, setStatus] = useState<FormStatus>('idle');

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('submitting');

    const form = e.currentTarget;
    const data = new FormData(form);

    try {
      const res = await fetch('https://formspree.io/f/xgonybzr', {
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json' },
      });

      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  }

  return (
    <div className="min-h-screen text-white">
      <div className="max-w-2xl mx-auto px-4 py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-10"
        >
          ← Back to OurSexQuiz
        </Link>

        <h1 className="text-3xl font-bold mb-2">Affiliate Program</h1>
        <p className="text-lg text-slate-400 mb-12">
          Earn money sharing something couples actually love.
        </p>

        <div className="space-y-10">
          {/* How it works */}
          <section>
            <h2 className="text-lg font-semibold text-white mb-4">How it works</h2>
            <ol className="space-y-3 text-slate-300 leading-relaxed">
              <li className="flex gap-3">
                <span className="font-semibold text-white shrink-0">1.</span>
                <span>
                  <span className="font-medium text-white">Apply below.</span> We review
                  applications and get back to you within a few days.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="font-semibold text-white shrink-0">2.</span>
                <span>
                  <span className="font-medium text-white">Get your referral link.</span> Once
                  approved, you receive a unique tracking link with 60-day attribution.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="font-semibold text-white shrink-0">3.</span>
                <span>
                  <span className="font-medium text-white">Earn 50% commission per sale.</span>{' '}
                  Every time someone unlocks their full results through your link, you earn a 50%
                  commission on the sale price.
                </span>
              </li>
            </ol>
            <p className="text-sm text-slate-400 mt-3">
              Payouts are processed monthly, once your balance exceeds {currencyConfig.symbol}50.
            </p>
          </section>

          {/* Who it's for */}
          <section>
            <h2 className="text-lg font-semibold text-white mb-3">Who it's for</h2>
            <p className="text-slate-300 leading-relaxed mb-3">
              This programme is a good fit if your audience cares about relationships, intimacy,
              couples, dating, or sexual wellness. Some examples of who typically applies:
            </p>
            <ul className="space-y-1 text-slate-300 leading-relaxed mb-4">
              {[
                'Relationship bloggers and writers',
                'Sex educators and therapists',
                'Dating and intimacy coaches',
                'Podcasters covering couples, sexuality, or wellness',
                'Social media creators (TikTok, Instagram, YouTube)',
                'Newsletter authors in the relationships or personal growth space',
              ].map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="text-primary shrink-0">—</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-slate-400 text-sm leading-relaxed">
              Not on this list? If your audience includes couples who care about intimacy and
              connection, you are likely a good fit. There is no minimum follower count, and we care
              more about relevance than reach.
            </p>
          </section>

          {/* FAQ */}
          <section>
            <h2 className="text-lg font-semibold text-white mb-4">Frequently asked questions</h2>
            <div className="space-y-6">
              {[
                {
                  q: 'How does tracking work?',
                  a: 'When someone visits OurSexQuiz through your referral link, the referral is saved in their browser. If they unlock their full results within 60 days, the sale is attributed to you.',
                },
                {
                  q: 'What counts as a conversion?',
                  a: 'A conversion happens when your referral pays to unlock their full results. Taking the free quiz does not earn a commission. Only the paid upgrade does.',
                },
                {
                  q: 'When and how do I get paid?',
                  a: `Payouts are processed monthly. Once your balance exceeds ${currencyConfig.symbol}50, earnings are sent to your preferred payment method. We support bank transfer and PayPal.`,
                },
                {
                  q: 'Is there a minimum audience size?',
                  a: "No. We care more about relevance than reach. If your audience includes couples interested in their relationship and sex life, they're exactly the right fit, regardless of how large that audience is.",
                },
                {
                  q: 'Can I promote OurSexQuiz in any language?',
                  a: "Yes. The quiz itself is in English, but you're welcome to promote it in any language to your audience.",
                },
              ].map(({ q, a }) => (
                <div key={q}>
                  <p className="font-medium text-white mb-1">{q}</p>
                  <p className="text-slate-300 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Application form */}
          <section>
            <h2 className="text-lg font-semibold text-white mb-4">Apply now</h2>

            {status === 'success' ? (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-center">
                <p className="text-emerald-400 font-semibold text-lg mb-1">Application received!</p>
                <p className="text-slate-300 text-sm">
                  Thanks for your interest. We'll review your application and get back to you within
                  a few days.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-1">
                    Email address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50"
                  />
                </div>

                <div>
                  <label
                    htmlFor="promotion-plan"
                    className="block text-sm font-medium text-slate-300 mb-1"
                  >
                    How do you plan to share OurSexQuiz?
                  </label>
                  <textarea
                    id="promotion-plan"
                    name="promotion-plan"
                    required
                    rows={4}
                    placeholder="e.g., I run a relationships blog with 10k monthly readers, or I have a dating advice TikTok with 50k followers"
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 resize-y"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-red-400 text-sm">
                    Something went wrong. Please try again or email us directly.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full rounded-lg bg-primary px-6 py-3 font-semibold text-white hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === 'submitting' ? 'Submitting…' : 'Submit Application'}
                </button>
              </form>
            )}
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-lg font-semibold text-white mb-2">Questions?</h2>
            <p className="text-slate-300">
              Email us at{' '}
              <a
                href="mailto:affiliates@oursexquiz.com"
                className="text-primary hover:underline underline-offset-2"
              >
                affiliates@oursexquiz.com
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
